import { Dialog } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useCreate, useNotify, useRecordContext, useUpdateMany } from 'react-admin';
import { tokens, useBrsCreateStyles } from '../pages/brs/BrsCreateStyles';
import { DestinationsSection } from '../pages/brs/components/DestinationsSection';
import { triggerAddonsUpdate } from '../helpers';

// AddonDialog restyled to match the BRS form aesthetic (cyan accent, dark
// cards, square corners) while keeping the original add-on save semantics:
//   - reactivate previously-inactive destinations the operator re-selects
//   - create new brsjobdestination rows for net-new destinations
//   - mark prior notifications as latest=false and create a fresh one
//   - call the workflow/update-addons trigger
//
// Destination editing reuses the same DestinationsSection used on the create
// form so behavior (chips on left, comma textarea on right, autocomplete) is
// identical across pages.
export const AddonDialog = ({ handleAddOnClose, handleRefresh }) => {
  const record = useRecordContext();
  const classes = useBrsCreateStyles();
  const notify = useNotify();
  const [create] = useCreate();
  const [updateMany] = useUpdateMany();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Seed the picker with currently-active destinations. The save handler
  // distinguishes net-new destinations from previously-inactive ones by
  // checking against record.destinations.
  const initialActive = useMemo(
    () =>
      (record?.destinations || [])
        .filter((d) => d.active)
        .map((d) => ({
          id: d.destination.id,
          name: d.destination.name,
          call_letters: d.destination.call_letters,
        })),
    [record],
  );
  const [selectedDestinations, setSelectedDestinations] = useState(initialActive);
  const [, setAntennaSDActive] = useState(false);

  useEffect(() => {
    setSelectedDestinations(initialActive);
  }, [initialActive]);

  const handleSave = async () => {
    if (selectedDestinations.length === 0) {
      notify('Add at least one destination before saving', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const existingByDestId = new Map(
        (record.destinations || []).map((d) => [d.destination.id, d]),
      );

      // Latest notification template attached to every changed destination.
      const trafficinstructions = record.trafficinstructions || [];
      const latestVersion =
        trafficinstructions.length > 0
          ? trafficinstructions.reduce((prev, current) =>
              Number(prev.version) > Number(current.version) ? prev : current,
            ).version
          : 'N/A';
      const newNotification = {
        latest: true,
        status: 'Pending',
        progress: 0,
        traffic_instruction_version: latestVersion,
      };

      // Bucket the selections.
      const toReactivate = []; // existing brsjobdestination rows with active=false
      const toCreate = []; // brand-new destinations
      const notificationsToInvalidate = [];

      for (const sel of selectedDestinations) {
        const existing = existingByDestId.get(sel.id);
        if (!existing) {
          toCreate.push(sel.id);
        } else if (!existing.active) {
          toReactivate.push(existing.id);
          for (const n of existing.notifications || []) {
            if (n.latest) notificationsToInvalidate.push({ ...n, latest: false });
          }
        }
      }

      if (notificationsToInvalidate.length > 0) {
        await updateMany(
          'brsnotification',
          notificationsToInvalidate.map((n) => n.id),
          notificationsToInvalidate,
        );
      }

      if (toReactivate.length > 0) {
        await updateMany(
          'brsjobdestination',
          toReactivate,
          toReactivate.map((id) => ({ id, active: true })),
        );
        for (const id of toReactivate) {
          create('brsnotification', { ...newNotification, brsjobdestination_id: id });
        }
      }

      if (toCreate.length > 0) {
        await new Promise((resolve, reject) => {
          create(
            'brsjobdestination',
            toCreate.map((id) => ({
              destination_id: id,
              brs_job_id: record.id,
              notifications: { data: newNotification },
            })),
            {
              onSuccess: () => resolve(),
              onFailure: (error) => reject(error),
            },
          );
        });
      }

      const reactivatedDestIds = toReactivate
        .map((rowId) =>
          (record.destinations || []).find((d) => d.id === rowId)?.destination?.id,
        )
        .filter(Boolean);
      await triggerAddonsUpdate(record.id, record.job_id, [
        ...toCreate,
        ...reactivatedDestIds,
      ]);

      notify('Add-ons updated', 'info');
      handleRefresh?.();
      handleAddOnClose?.();
    } catch (error) {
      notify(`Failed to update add-ons: ${error?.message || error}`, 'warning');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={handleAddOnClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        style: {
          background: tokens.bgDark,
          color: tokens.text,
          border: `1px solid ${tokens.border}`,
          borderRadius: 0,
        },
      }}>
      <div className={classes.content} style={{ padding: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: tokens.text }}>
            Add-Ons
          </h2>
          <button
            type="button"
            onClick={handleAddOnClose}
            style={{
              background: 'none',
              border: 'none',
              color: tokens.textDim,
              cursor: 'pointer',
              fontSize: 22,
              lineHeight: 1,
              padding: 0,
            }}
            aria-label="Close">
            &times;
          </button>
        </div>

        <div className={classes.sectionHeader}>Request</div>
        <div className={classes.card}>
          <div className={classes.formRow}>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Request ID</label>
              <input
                className={`${classes.formInput} ${classes.formInputReadonly}`}
                value={record?.request_id || ''}
                readOnly
              />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Estimate ID</label>
              <input
                className={`${classes.formInput} ${classes.formInputReadonly}`}
                value={record?.estimate_id || ''}
                readOnly
              />
            </div>
          </div>
        </div>

        <DestinationsSection
          selectedDestinations={selectedDestinations}
          onDestinationsChange={setSelectedDestinations}
          onAntennaSDChange={setAntennaSDActive}
        />

        <div className={classes.formFooter} style={{ marginTop: 12 }}>
          <div className={classes.helpText}>
            Selecting a previously-removed destination will reactivate it.
          </div>
          <div className={classes.footerActions}>
            <button
              type="button"
              className={`${classes.btn} ${classes.btnGhost}`}
              onClick={handleAddOnClose}
              disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className={`${classes.btn} ${classes.btnPrimary}`}
              onClick={handleSave}
              disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

AddonDialog.propTypes = {
  handleAddOnClose: PropTypes.func,
  handleRefresh: PropTypes.func,
};
